import { useState, useEffect } from 'react';
import {
  Button,
  Textarea,
  Spinner,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import { useUpdateAdminNotes } from '@/api/hooks/useReferrals';
import type { ReferralDto } from '@/types/referral';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
  },
});

interface AdminNotesSectionProps {
  referral: ReferralDto;
  performedBy: string;
  performedByName: string;
}

export function AdminNotesSection({
  referral,
  performedBy,
  performedByName,
}: AdminNotesSectionProps) {
  const styles = useStyles();
  const updateNotes = useUpdateAdminNotes();
  const [notes, setNotes] = useState(referral.adminNotes ?? '');

  useEffect(() => {
    setNotes(referral.adminNotes ?? '');
  }, [referral.adminNotes]);

  const isDirty = notes !== (referral.adminNotes ?? '');

  const handleSave = async () => {
    await updateNotes.mutateAsync({
      id: referral.id,
      data: { adminNotes: notes },
      performedBy,
      performedByName,
    });
  };

  return (
    <div className={styles.root}>
      <Textarea
        value={notes}
        onChange={(_e, d) => setNotes(d.value)}
        placeholder="Internal notes (admin only)..."
        rows={4}
        resize="vertical"
      />
      <div className={styles.footer}>
        <Text className={styles.label}>Admin only — not visible to referrers</Text>
        <Button
          appearance="primary"
          size="small"
          disabled={!isDirty || updateNotes.isPending}
          onClick={() => void handleSave()}
          icon={updateNotes.isPending ? <Spinner size="tiny" /> : undefined}
        >
          {updateNotes.isPending ? 'Saving...' : 'Save Notes'}
        </Button>
      </div>
    </div>
  );
}
